import type { Params } from './flow-steps.tsx';

export interface FlowNode {
  id: string;
  params?: Params;
  path: string;
  prev: FlowNode | null; // First-writer-wins: a -> b & a -> c -> d -> b = b <- a
  next: FlowNode | null;
  back: FlowNode | null; // custom back target from step.back?, resolved at graph build time
}

export class FlowGraph {
  private nodes = new Map<string, FlowNode>();

  ensure(path: string, step: { id: string; params?: Params }): boolean {
    if (!this.nodes.has(path)) {
      this.nodes.set(path, {
        path,
        prev: null,
        next: null,
        back: null,
        id: step.id,
        params: step.params,
      });
      return true;
    }
    return false;
  }

  getNode(path: string): FlowNode | undefined {
    if (!this.nodes.has(path)) {
      return undefined;
    }
    return this.nodes.get(path);
  }

  linkPrev(from: string, to: string): void {
    const fromNode = this.#retrieveNode(from);
    const toNode = this.#retrieveNode(to);

    if (toNode.prev === null) {
      toNode.prev = fromNode; // First-writer-wins
    }
  }

  linkBack(from: string, to: string): void {
    const fromNode = this.#retrieveNode(from);
    const toNode = this.#retrieveNode(to);
    fromNode.back = toNode;
  }

  linkNext(from: string, to: string): void {
    const fromNode = this.#retrieveNode(from);
    const toNode = this.#retrieveNode(to);

    if (fromNode.next === null) {
      fromNode.next = toNode; // First-writer-wins
    }
  }

  discovered(): Set<string> {
    return new Set(this.nodes.keys());
  }

  lastNode(node: FlowNode, discovered = new Set<string>()): FlowNode {
    if (node.next === null) {
      return node;
    }
    if (!discovered.has(node.path)) {
      return node;
    }
    if (discovered.has(node.next.path)) {
      return node.next; // Prevent cycles
    }
    return this.lastNode(node.next, discovered.add(node.path));
  }

  #retrieveNode(path: string): FlowNode {
    const node = this.getNode(path);
    if (node === undefined) {
      throw new Error(`Node with path "${path}" does not exist.`);
    }
    return node;
  }
}
